import type { PropertyName } from "@frui.ts/helpers";
import { bound } from "@frui.ts/helpers";
import { Router, watchBusy } from "@frui.ts/screens";
import type ScreenBase from "@frui.ts/screens/dist/structure/screenBase";
import { Modules } from "config";
import type RecipientBankAccount from "entities/recipientBankAccount";
import TransferType from "entities/transferType";
import type SenderBankAccount from "entities/refund/senderBankAccount";
import type UnpairedPayment from "entities/unpairedPayment";
import type { interfaces } from "inversify";
import { computed } from "mobx";
import PaymentsPairingRepository from "repositories/paymentsPairingRepository";
import ConfirmationService from "services/confirmationService";
import EventBus, { paymentRefunded } from "services/eventBus";
import LocalizationService from "services/localizationService";
import NotificationService from "services/notificationService";
import AppRouter from "services/router";
import SecurityService from "services/securityService";
import DetailViewModelWithChildren from "viewModels/detailViewModelWithChildren";
import UnpairedPaymentStatus from "entities/unpairedPaymentStatus";
import { allowToRefund } from "../rights";
import PairingByPaymentListViewModel from "./pairingByPaymentListViewModel";
import PairingByPaymentOrdersViewModel from "./pairingByPaymentOrdersViewModel";

@Router.registerRoute({ name: Router.Self, route: ":paymentId" })
export default class PairingByPaymentDetailViewModel extends DetailViewModelWithChildren<UnpairedPayment, ScreenBase> {
  constructor(
    private itemId: string,
    private matchingByPaymentOrdersViewModelFactory: ReturnType<typeof PairingByPaymentOrdersViewModel.Factory>,
    private repository: PaymentsPairingRepository,
    private localizationService: LocalizationService,
    private confirmationService: ConfirmationService,
    private securityService: SecurityService,
    private notificationService: NotificationService,
    private eventBus: EventBus,
    private appRouter: AppRouter
  ) {
    super();
    this.navigationName = this.itemId;
  }

  async onInitialize(): Promise<void> {
    await super.onInitialize();
    this.children.push(this.matchingByPaymentOrdersViewModelFactory(this.item));
    this.tryActivateChild(this.children[0]);
  }

  get navigationParams(): any {
    return undefined;
  }

  @watchBusy
  protected loadDetail(): Promise<UnpairedPayment> {
    return this.repository.getUnpairedPayment(this.itemId);
  }

  @bound
  close() {
    this.requestClose();
  }

  @computed
  get backLink() {
    return this.appRouter.getUrl(PairingByPaymentListViewModel);
  }

  @bound
  transferPaymentTypeName(paymentType: TransferType) {
    return this.localizationService.translateEnum("transferType", paymentType);
  }

  @bound
  async onCreateRefundClick() {
    if (!this.allowToRefund) {
      this.notificationService.addNotAllowedError();
      return;
    }

    const confirmed = await this.confirmationService.showConfirmation(this.translate("confirmRefund.title"), {
      confirmButton: { text: this.translate("confirmRefund.confirm") },
    });

    if (confirmed) {
      try {
        await this.repository.refundPayment(this.item);
        this.item.status = UnpairedPaymentStatus.RefundCreated;
        this.eventBus.publish(paymentRefunded({ payment: this.item }));
      } catch (e) {
        // General error handle is good enough
      }
    }
  }

  @computed
  get allowToRefund() {
    return allowToRefund(this.securityService);
  }

  translate(code: string, size?: number) {
    return this.localizationService.translateModule(Modules.finance, `paymentsPairing.${code}`, size);
  }

  translateAttribute(model: "unpairedPayment", attribute: PropertyName<UnpairedPayment>): string;
  translateAttribute(model: "recipientBankAccount", attribute: PropertyName<RecipientBankAccount>): string;
  translateAttribute(model: "senderBankAccount", attribute: PropertyName<SenderBankAccount>): string;
  translateAttribute(model: "unpairedPayment" | "recipientBankAccount" | "senderBankAccount", attribute: any) {
    return this.localizationService.translateAttribute(model, attribute);
  }

  translateStatus(status: UnpairedPaymentStatus) {
    return this.localizationService.translateEnum("unpairedPaymentStatus", status);
  }

  static Factory({ container }: interfaces.Context) {
    return (paymentId: string) =>
      new PairingByPaymentDetailViewModel(
        paymentId,
        container.get(PairingByPaymentOrdersViewModel.Factory),
        container.get(PaymentsPairingRepository),
        container.get(LocalizationService),
        container.get(ConfirmationService),
        container.get(SecurityService),
        container.get(NotificationService),
        container.get(EventBus),
        container.get(AppRouter)
      );
  }
}
